import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { 
  Settings, 
  BookOpen, 
  MessageSquare, 
  Rocket,
  Bot,
  ChevronRight,
  ExternalLink,
  Sun,
  Moon,
  UserCheck,
  SlidersHorizontal,
  ScrollText
} from 'lucide-react';
import ChatAgentKnowledgeBase from './ChatAgentKnowledgeBase';
import ChatbotIntentsListing from './ChatIntents/ChatbotIntentsListing';
import CoreSettings, { type AgentType } from './CoreSettings';
import chatAgentService from './ChatIntents/chatAgentService';
import ChatAgentSecurity from './ChatAgentSecurity';
import ChatAgentDeployment from './ChatAgentDeployment';
import ChatAgentSimulation from './ChatAgentSimulation';
import ChatAgentSetting from './ChatAgentSetting';

// Interfaces
type Theme = 'dark' | 'light';

interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  border: string;
  text: {
    primary: string;
    secondary: string;
    muted: string;
  };
  accent: string;
  hover: string;
  input: string;
}

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
}

interface ChatAgentGeneratorSidebarProps {
  activeItem: string;
  onItemClick: (itemId: string) => void;
  onBackToChatbots: () => void;
  agentUrl?: string;
  sidebarItems: SidebarItem[];
  theme: Theme;
  onThemeToggle: () => void;
  isEditMode?: boolean; // If false, only core-settings is enabled
  isAdvancedAgent?: boolean; // Intents only available for Advanced agents
}

interface ChatAgentGenerationLayoutProps {
  children?: React.ReactNode;
  tenantId?: string;
  userId?: string;
  chatbotId?: string;
}

interface DummyContentProps {
  activeItem: string;
  theme: Theme;
}

// Theme Configuration
const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-300',
      muted: 'text-neutral-400'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-800',
    input: 'bg-neutral-700 border-neutral-600 text-white'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: {
      primary: 'text-gray-900',
      secondary: 'text-gray-700',
      muted: 'text-gray-500'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900'
  }
};

// Sidebar Items Configuration
const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    id: 'core-settings',
    label: 'General',
    icon: <Settings className="w-4 h-4" />,
    description: 'Identity, model, and behavior'
  },
  {
    id: 'knowledge-base',
    label: 'Knowledge Base',
    icon: <BookOpen className="w-4 h-4" />,
    description: 'Upload and manage agent knowledge sources'
  },
  {
    id: 'intents',
    label: 'Intents',
    icon: <MessageSquare className="w-4 h-4" />,
    description: 'Define conversation intents and triggers'
  },
  {
    id: 'Security',
    label: 'Security',
    icon: <UserCheck className="w-4 h-4" />,
    description: 'Manage ChatBot Security'
  },
  {
    id: 'simulation',
    label: 'Usage logs',
    icon: <ScrollText className="w-4 h-4" />,
    description: 'Users, queries, and token usage'
  },
  {
    id: 'setting',
    label: 'Setting',
    icon: <SlidersHorizontal className="w-4 h-4" />,
    description: 'Feature flags, defaults, and panel visibility'
  },
  {
    id: 'deployment',
    label: 'Deployment',
    icon: <Rocket className="w-4 h-4" />,
    description: 'Deploy agent to production environments'
  }
];

// Sidebar Component
const ChatAgentGeneratorSidebar: React.FC<ChatAgentGeneratorSidebarProps> = ({
  activeItem,
  onItemClick,
  onBackToChatbots,
  agentUrl,
  sidebarItems,
  theme,
  onThemeToggle,
  isEditMode = false,
  isAdvancedAgent = false
}) => {
  const colors = THEME_CONFIG[theme];
  
  // Check if an item is enabled (only core-settings in create mode)
  const isItemEnabled = (itemId: string) => {
    return isEditMode || itemId === 'core-settings';
  };

  const visibleItems = sidebarItems.filter(
    (item) => item.id !== 'intents' || isAdvancedAgent
  );

  return (
    <div className={`w-64 h-full ${colors.primary} ${colors.border} border-r flex flex-col`}>
      <div className={`border-b px-3 py-1.5 ${colors.border} ${colors.secondary}`}>
        <nav aria-label="Breadcrumb" className={`flex items-center gap-1 text-[11px] ${colors.text.muted}`}>
          <button
            type="button"
            onClick={onBackToChatbots}
            className={`rounded-sm transition-colors hover:text-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${colors.text.secondary}`}
          >
            Chatbots
          </button>
          <ChevronRight className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
          <span aria-current="page" className="truncate">Copilot Builder</span>
        </nav>
      </div>

      <div className={`px-4 py-3 ${colors.border} border-b`}>
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 flex-shrink-0 text-indigo-400" />
          <h1 className={`min-w-0 flex-1 truncate text-sm font-medium ${colors.text.primary}`}>
            Copilot Builder
          </h1>
          {agentUrl && (
            <a
              href={agentUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open agent in a new tab"
              title="Open agent in a new tab"
              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border transition-colors hover:border-indigo-500 hover:text-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${colors.border} ${colors.text.secondary}`}
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          )}
        </div>
        <p className={`text-xs ${colors.text.muted} mt-1`}>Configure your intelligent agent</p>
      </div>

      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const enabled = isItemEnabled(item.id);
          return (
            <button
              key={item.id}
              onClick={() => enabled && onItemClick(item.id)}
              disabled={!enabled}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors text-sm group ${
                !enabled
                  ? 'opacity-50 cursor-not-allowed'
                  : activeItem === item.id
                  ? `${colors.accent} text-white`
                  : `${colors.text.secondary} ${colors.hover} ${theme === 'light' ? 'hover:text-gray-900' : 'hover:text-white'}`
              }`}
            >
              <span className={`${
                !enabled
                  ? colors.text.muted
                  : activeItem === item.id 
                  ? 'text-white' 
                  : `${colors.text.muted} ${theme === 'light' ? 'group-hover:text-indigo-600' : 'group-hover:text-indigo-400'}`
              }`}>
                {item.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.label}</div>
                {item.description && (
                  <div className={`mt-0.5 truncate text-xs ${
                    activeItem === item.id ? 'text-indigo-100' : colors.text.muted
                  }`}>
                    {item.description}
                  </div>
                )}
              </div>
              <ChevronRight className={`w-3 h-3 transition-transform ${
                activeItem === item.id ? 'rotate-90' : enabled ? 'group-hover:translate-x-0.5' : ''
              }`} />
            </button>
          );
        })}
      </nav>

      <div className={`px-4 py-3 ${colors.border} border-t space-y-3`}>
        <button
          onClick={onThemeToggle}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${colors.hover} ${colors.text.secondary}`}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          <span className="text-sm">
            {theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
          </span>
        </button>

        <div className={`text-xs ${colors.text.muted}`}>
          <div>Version 1.0.0</div>
          <div className="mt-1">Agent Builder Portal</div>
        </div>
      </div>
    </div>
  );
};

// Dummy Content Component
const DummyContent: React.FC<DummyContentProps> = ({ activeItem, theme }) => {
  const colors = THEME_CONFIG[theme];

  const getContentForItem = (itemId: string) => {
    switch (itemId) { 
      case 'simulation':
        return {
          title: 'Usage logs',
          content: 'Review who used this agent, what they asked, and how many tokens were consumed.'
        };
      case 'deployment':
        return {
          title: 'Deployment Configuration',
          content: 'Deploy your agent to various channels and production environments.'
        };
      default:
        return {
          title: 'Welcome',
          content: 'Select an option from the sidebar to get started with building your chat agent.'
        };
    }
  };

  const { title, content } = getContentForItem(activeItem);
  const item = SIDEBAR_ITEMS.find(i => i.id === activeItem);

  return (
    <div className="flex-1 flex flex-col">
      <div className={`px-6 py-4 ${colors.border} border-b ${colors.primary}`}>
        <h2 className={`text-lg font-semibold ${colors.text.primary}`}>{title}</h2>
        <p className={`text-sm ${colors.text.muted} mt-1`}>{content}</p>
      </div>

      <div className={`flex-1 p-6 ${colors.background}`}>
        <div className="max-w-4xl">
          <div className={`${colors.surface} rounded-lg p-6 border ${colors.border} ${theme === 'light' ? 'shadow-sm' : ''}`}>
            <div className="text-center py-12">
              <div className={`w-16 h-16 ${theme === 'light' ? 'bg-gray-100' : 'bg-neutral-700'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                {item?.icon || <Settings className={`w-8 h-8 ${colors.text.muted}`} />}
              </div>
              <h3 className={`text-xl font-medium ${colors.text.primary} mb-2`}>{title}</h3>
              <p className={`${colors.text.muted} text-sm max-w-md mx-auto leading-relaxed`}>
                {content}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Layout Component
const ChatAgentGenerationLayout: React.FC<ChatAgentGenerationLayoutProps> = ({ 
  children,
  tenantId = '00000000-0000-0000-0000-000000000000',
  userId = '00000000-0000-0000-0000-000000000000',
  chatbotId
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState<string>('core-settings');
  const [theme, setTheme] = useState<Theme>('dark');
  const [agentType, setAgentType] = useState<AgentType>('Basic');
  const [agentTypeLoaded, setAgentTypeLoaded] = useState(false);
  const [publicAgentId, setPublicAgentId] = useState<string | null>(null);

  const isIntentListRoute = /\/intents\/?$/.test(location.pathname);
  const isAdvancedAgent = agentType === 'Advanced';

  useEffect(() => {
    const activeTheme = document.documentElement.getAttribute('data-theme');
    setTheme(activeTheme === 'white' ? 'light' : 'dark');
  }, []);

  useEffect(() => {
    if (!chatbotId) {
      setAgentType('Basic');
      setAgentTypeLoaded(true);
      setPublicAgentId(null);
      return;
    }

    let cancelled = false;
    setAgentTypeLoaded(false);

    const loadAgentType = async () => {
      try {
        const response = await chatAgentService.getAgentById(chatbotId);
        if (cancelled) return;
        const type = response?.data?.agent_type;
        const loadedPublicAgentId = response?.data?.agent_id;
        setPublicAgentId(
          typeof loadedPublicAgentId === 'string' && loadedPublicAgentId.trim()
            ? loadedPublicAgentId.trim()
            : null
        );
        setAgentType(
          typeof type === 'string' && type.toLowerCase() === 'advanced'
            ? 'Advanced'
            : 'Basic'
        );
      } catch {
        if (!cancelled) {
          setAgentType('Basic');
          setPublicAgentId(null);
        }
      } finally {
        if (!cancelled) setAgentTypeLoaded(true);
      }
    };

    void loadAgentType();
    return () => {
      cancelled = true;
    };
  }, [chatbotId]);

  useEffect(() => {
    if (isIntentListRoute && isAdvancedAgent) {
      setActiveItem('intents');
    }
  }, [isIntentListRoute, isAdvancedAgent]);

  // Redirect away from intents when agent is not Advanced (after type is known)
  useEffect(() => {
    if (!agentTypeLoaded || !chatbotId || !isIntentListRoute || isAdvancedAgent) {
      return;
    }
    navigate(`/portal/chatbot/${chatbotId}`, { replace: true });
    setActiveItem('core-settings');
  }, [agentTypeLoaded, chatbotId, isIntentListRoute, isAdvancedAgent, navigate]);

  // Reset to core-settings when chatbotId changes (for edit mode)
  useEffect(() => {
    if (chatbotId && !isIntentListRoute) {
      setActiveItem('core-settings');
    }
  }, [chatbotId, isIntentListRoute]);

  // Leave intents tab if agent type is switched to Basic
  useEffect(() => {
    if (agentTypeLoaded && !isAdvancedAgent && activeItem === 'intents') {
      setActiveItem('core-settings');
    }
  }, [agentTypeLoaded, isAdvancedAgent, activeItem]);

  const handleItemClick = (itemId: string) => {
    // Only allow navigation to core-settings in create mode
    if (!chatbotId && itemId !== 'core-settings') {
      return;
    }

    if (itemId === 'intents') {
      if (!isAdvancedAgent || !chatbotId) return;
      navigate(`/portal/chatbot/${chatbotId}/intents`);
      setActiveItem('intents');
      return;
    }

    if (chatbotId && isIntentListRoute) {
      navigate(`/portal/chatbot/${chatbotId}`);
    }

    setActiveItem(itemId);
  };

  const handleThemeToggle = () => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next === 'light' ? 'white' : 'black');
      localStorage.setItem('portal_theme', next === 'light' ? 'white' : 'black');
      return next;
    });
  };

  // ===== HANDLE SAVE - LAYOUT LEVEL =====
  const handleSave = async (settings: any): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const payload = {
        agent_type: settings.agentType,
        agent_id: settings.agentId,
        languages: settings.languages,
        title: settings.title,
        description: settings.description,
        icon_path: settings.icon || '',
        roles: [settings.role],
        llm_id: '7d65eaa2-334b-4a64-bac7-b9f47c19cbbf',
        fo_url: settings.foUrl || '',
        chat_type: settings.chatType || 'text',
        created_by: userId,
        tenant_id: tenantId
      };

      console.log('=== Layout: Payload being sent ===', payload);
      
      const response = await chatAgentService.createChatAgent(payload);
      
      console.log('=== Layout: Agent created successfully ===', response);
      
      return { success: true, data: response };
      
    } catch (error: any) {
      console.error('=== Layout: Error creating chat agent ===', error);
      return { success: false, error: error.message || 'Failed to create chat agent' };
    }
  };

  const colors = THEME_CONFIG[theme];

  // Render content based on active item
  const renderContent = () => {
    if (activeItem === 'core-settings') {
      return (
        <CoreSettings 
          theme={theme}
          existingAgentId={chatbotId}
          mode={chatbotId ? 'edit' : 'create'}
          onAgentTypeChange={setAgentType}
        />
      );
    }
    else if (activeItem === 'setting') {
      return <ChatAgentSetting theme={theme} agentId={chatbotId || ''} />;
    }
    else if (activeItem === 'knowledge-base') {
      return <ChatAgentKnowledgeBase theme={theme} agentId={chatbotId || ''} />;
    }
    else if (activeItem === 'intents' && !isIntentListRoute) {
      if (chatbotId) {
        return null;
      }
      return <ChatbotIntentsListing theme={theme} />;
    }
    else if (activeItem === 'Security') {
      return <ChatAgentSecurity theme={theme}    />;
    }
    else if (activeItem === 'deployment') {
      return <ChatAgentDeployment theme={theme} agentId={chatbotId || ''} tenantId={tenantId} />;
    }
    else if (activeItem === 'simulation') {
      return <ChatAgentSimulation theme={theme} agentId={chatbotId || ''} />;
    }
    return <DummyContent activeItem={activeItem} theme={theme} />;
  };

  return (
    <div className={`h-[calc(100vh-1.5rem)] min-h-0 ${colors.background} flex overflow-hidden`}>
      <ChatAgentGeneratorSidebar
        activeItem={activeItem}
        onItemClick={handleItemClick}
        onBackToChatbots={() => navigate('/portal/chatagent')}
        agentUrl={publicAgentId ? `https://chat.selficai.com/${encodeURIComponent(publicAgentId)}` : undefined}
        sidebarItems={SIDEBAR_ITEMS}
        theme={theme}
        onThemeToggle={handleThemeToggle}
        isEditMode={!!chatbotId}
        isAdvancedAgent={isAdvancedAgent}
      />
      
      <div className="flex-1 flex flex-col overflow-y-auto">
        {isIntentListRoute && agentTypeLoaded && isAdvancedAgent
          ? <Outlet />
          : (children || renderContent())}
      </div>
    </div>
  );
};

// Main Component Export
const ChatAgentGenerator: React.FC = () => {
  // Get chatbotId from route params
  const { chatbotId } = useParams<{ chatbotId?: string }>();
  
  // TODO: Get these from your auth context
  const tenantId = '00000000-0000-0000-0000-000000000000';
  const userId = '00000000-0000-0000-0000-000000000000';
  
  return (
    <ChatAgentGenerationLayout 
      tenantId={tenantId}
      userId={userId}
      chatbotId={chatbotId}
    />
  );
};

export default ChatAgentGenerator;
 